import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, RefreshCw, Settings2, History, Bell, ChevronRight, X, Download } from "lucide-react";

const STATUS_META = {
  active:      { label: "ACTIVE",      cls: "bg-emerald-500 text-white" },
  beta:        { label: "BETA",        cls: "bg-amber-500 text-white" },
  coming_soon: { label: "COMING SOON", cls: "bg-sky-500 text-white" },
  disabled:    { label: "DISABLED",    cls: "bg-rose-500 text-white" },
};
const CATEGORY_LABELS = { hotel: "Hotels", cab: "Cabs", bus: "Buses", package: "Packages" };

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-gray-400 text-white" };
  return <Badge className={`${m.cls} text-[10px] font-bold`} data-testid={`status-${status}`}>{m.label}</Badge>;
}

function ProgressBar({ count, threshold }) {
  const pct = Math.min(100, Math.round((count / Math.max(1, threshold)) * 100));
  const color = pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-sky-400";
  return (
    <div className="w-full">
      <div className="h-1.5 w-full bg-[#E5E3DB] dark:bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-[#8A998E] mt-0.5">{count}/{threshold}</div>
    </div>
  );
}

export default function DestinationsTab() {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [overrideModal, setOverrideModal] = useState(null);    // { destination, category }
  const [auditDrawer, setAuditDrawer] = useState(null);        // destination
  const [waitlistDrawer, setWaitlistDrawer] = useState(null);  // destination

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/destinations");
      setDestinations(r.data.destinations || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    if (!window.confirm("Seed the cities catalog? This is idempotent — existing cities are updated, not duplicated.")) return;
    try {
      const r = await api.post("/admin/destinations/seed");
      toast.success(`Seeded — ${r.data.inserted} new · ${r.data.updated} updated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
  };

  const filtered = destinations.filter((d) => {
    if (search && !d.name.toLowerCase().includes(search.toLowerCase()) && !d.slug.includes(search.toLowerCase())) return false;
    if (filter === "all") return true;
    return d.primary_status === filter;
  });

  return (
    <div className="space-y-4" data-testid="destinations-tab">
      {/* Filters bar */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search city or slug…"
            className="w-56 h-9 bg-white dark:bg-zinc-900 border-[#E5E3DB] dark:border-zinc-700"
            data-testid="dest-search"
          />
          {["all", "active", "beta", "coming_soon", "disabled"].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filter === s ? "default" : "outline"}
              onClick={() => setFilter(s)}
              className={filter === s ? "bg-[#2C5F2D] text-white" : "border-[#E5E3DB] dark:border-zinc-700"}
              data-testid={`dest-filter-${s}`}
            >
              {s === "all" ? "All" : STATUS_META[s].label}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} className="border-[#E5E3DB] dark:border-zinc-700" data-testid="dest-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button size="sm" onClick={handleSeed} className="bg-[#2C5F2D] text-white" data-testid="dest-seed">
            Seed Catalog
          </Button>
        </div>
      </div>

      {/* Cities table */}
      {loading ? (
        <div className="text-center py-10 text-[#8A998E]">Loading destinations…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 bg-white dark:bg-zinc-900 rounded-xl border border-[#E5E3DB] dark:border-zinc-800">
          <MapPin className="w-10 h-10 mx-auto mb-3 text-[#8A998E] opacity-40" />
          <p className="text-sm text-[#8A998E]">No destinations match your filter.</p>
          {destinations.length === 0 && <p className="text-xs text-[#8A998E] mt-1">Tip: click "Seed Catalog" to load the master city list.</p>}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white dark:bg-zinc-900 rounded-xl border border-[#E5E3DB] dark:border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F2EE] dark:bg-zinc-800 text-[#444] dark:text-zinc-300">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">City</th>
                <th className="text-left px-3 py-2 font-semibold">Tier</th>
                <th className="text-left px-3 py-2 font-semibold">Primary</th>
                {Object.keys(CATEGORY_LABELS).map((c) => (
                  <th key={c} className="text-left px-3 py-2 font-semibold">{CATEGORY_LABELS[c]}</th>
                ))}
                <th className="text-left px-3 py-2 font-semibold">Waitlist</th>
                <th className="text-right px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.slug} className="border-t border-[#E5E3DB] dark:border-zinc-800 hover:bg-[#FAFAF7] dark:hover:bg-zinc-800/50" data-testid={`dest-row-${d.slug}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-[#262626] dark:text-zinc-100">{d.name}</div>
                    <div className="text-[11px] text-[#8A998E]">{d.state} · /{d.slug}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className="text-[10px] uppercase border-[#E5E3DB] dark:border-zinc-700">{d.pop_tier}</Badge>
                  </td>
                  <td className="px-3 py-2.5"><StatusPill status={d.primary_status} /></td>
                  {Object.keys(CATEGORY_LABELS).map((cat) => {
                    const c = d.categories[cat];
                    return (
                      <td key={cat} className="px-3 py-2.5 min-w-[120px]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <StatusPill status={c.effective_status} />
                          {c.override && <span title="Manual override" className="text-[10px] text-amber-600">⚙</span>}
                        </div>
                        <ProgressBar count={Math.min(c.listings_count, c.partners_count)} threshold={c.threshold} />
                        <button
                          onClick={() => setOverrideModal({ destination: d, category: cat })}
                          className="text-[11px] text-[#2C5F2D] hover:underline mt-0.5"
                          data-testid={`override-${d.slug}-${cat}`}
                        >
                          Override
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5">
                    <button
                      onClick={() => setWaitlistDrawer(d)}
                      className="flex items-center gap-1 text-xs text-[#2C5F2D] hover:underline disabled:opacity-50"
                      disabled={!d.waitlist_count}
                      data-testid={`waitlist-${d.slug}`}
                    >
                      <Bell className="w-3 h-3" /> {d.waitlist_count || 0}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => setAuditDrawer(d)}
                      className="text-xs text-[#666] hover:text-[#2C5F2D] inline-flex items-center gap-1"
                      data-testid={`audit-${d.slug}`}
                    >
                      <History className="w-3 h-3" /> Audit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {overrideModal && (
        <OverrideModal
          destination={overrideModal.destination}
          category={overrideModal.category}
          onClose={() => setOverrideModal(null)}
          onSaved={() => { setOverrideModal(null); load(); }}
        />
      )}
      {auditDrawer && <AuditDrawer destination={auditDrawer} onClose={() => setAuditDrawer(null)} />}
      {waitlistDrawer && <WaitlistDrawer destination={waitlistDrawer} onClose={() => setWaitlistDrawer(null)} />}
    </div>
  );
}

function OverrideModal({ destination, category, onClose, onSaved }) {
  const cat = destination.categories[category];
  const [status, setStatus] = useState(cat.override || "auto");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (reason.trim().length < 3) return toast.error("Reason is required (min 3 characters)");
    setSaving(true);
    try {
      await api.put(`/admin/destinations/${destination.slug}/override`, {
        category, status, reason: reason.trim(),
      });
      toast.success(`${destination.name} · ${CATEGORY_LABELS[category]} → ${status.toUpperCase()}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Override failed"); setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-zinc-900 max-w-md">
        <DialogHeader>
          <DialogTitle>Override · {destination.name} · {CATEGORY_LABELS[category]}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="text-xs text-[#8A998E] bg-[#F3F2EE] dark:bg-zinc-800 rounded-lg p-2.5">
            Auto-status: <strong className="text-[#262626] dark:text-zinc-100">{STATUS_META[cat.auto_status].label}</strong> ·
            {' '}{cat.listings_count} listings, {cat.partners_count} partners (threshold: {cat.threshold})
          </div>
          <div>
            <Label className="text-xs">Mode</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {["auto", "active", "beta", "coming_soon", "disabled"].map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={status === s ? "default" : "outline"}
                  onClick={() => setStatus(s)}
                  className={`text-xs ${status === s ? "bg-[#2C5F2D] text-white" : "border-[#E5E3DB] dark:border-zinc-700"}`}
                  data-testid={`override-status-${s}`}
                >
                  {s === "auto" ? "AUTO" : STATUS_META[s].label}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason (audit log) *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Soft-launch promo / Quality audit pause / Legal hold…"
              className="mt-1 bg-white dark:bg-zinc-800 border-[#E5E3DB] dark:border-zinc-700 text-sm min-h-[70px]"
              data-testid="override-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E3DB] dark:border-zinc-700" data-testid="override-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || reason.trim().length < 3} className="bg-[#2C5F2D] text-white" data-testid="override-save">
            {saving ? "Saving…" : "Save override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditDrawer({ destination, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/admin/destinations/${destination.slug}/audit`)
      .then((r) => setEntries(r.data.entries || []))
      .catch(() => toast.error("Failed to load audit"))
      .finally(() => setLoading(false));
  }, [destination.slug]);
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-zinc-900 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Audit log · {destination.name}</DialogTitle>
        </DialogHeader>
        {loading ? <div className="py-6 text-center text-[#8A998E]">Loading…</div> : entries.length === 0 ? (
          <div className="py-6 text-center text-[#8A998E] text-sm">No override history yet.</div>
        ) : (
          <div className="space-y-2">
            {entries.map((e) => (
              <div key={e.id} className="border border-[#E5E3DB] dark:border-zinc-800 rounded-lg p-3 text-sm" data-testid={`audit-entry-${e.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <Badge variant="outline" className="text-[10px] mr-1.5">{CATEGORY_LABELS[e.category] || e.category}</Badge>
                    <span className="text-[#666] dark:text-zinc-400 text-xs">
                      {(e.from_override || "auto").toUpperCase()} → {(e.to_override || "auto").toUpperCase()}
                    </span>
                  </div>
                  <span className="text-[10px] text-[#8A998E]">{new Date(e.created_at).toLocaleString()}</span>
                </div>
                <p className="mt-1.5 text-[#262626] dark:text-zinc-100">{e.reason}</p>
                <p className="text-[10px] text-[#8A998E] mt-0.5">by {e.admin_email}</p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WaitlistDrawer({ destination, onClose, onBlasted }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blasting, setBlasting] = useState(false);
  const pending = entries.filter((e) => e.status === "pending").length;
  const notified = entries.filter((e) => e.status === "notified").length;
  const isActive = destination.primary_status === "active";
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/destinations/${destination.slug}/waitlist`)
      .then((r) => setEntries(r.data.entries || []))
      .catch(() => toast.error("Failed to load waitlist"))
      .finally(() => setLoading(false));
  }, [destination.slug]);
  useEffect(() => { load(); }, [load]);

  const blast = async () => {
    if (!window.confirm(`Send launch email to ${pending} pending subscriber(s) for ${destination.name}? They will receive a "now live" email with ₹100 credit mention.`)) return;
    setBlasting(true);
    try {
      const r = await api.post(`/admin/destinations/${destination.slug}/notify-waitlist`);
      toast.success(`Launch email sent to ${r.data.sent} subscriber(s)`);
      load();
      if (onBlasted) onBlasted();
    } catch (e) { toast.error(e.response?.data?.detail || "Blast failed"); }
    finally { setBlasting(false); }
  };

  const exportCsv = () => {
    const rows = [["Email", "Phone", "Travel date", "Category", "Status", "Notified at", "Subscribed at"]];
    entries.forEach((e) => rows.push([e.email, e.phone || "", e.travel_date || "", e.category || "", e.status || "", e.notified_at || "", e.created_at]));
    const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `waitlist-${destination.slug}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 200);
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-zinc-900 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 flex-wrap">
            <span>Notify Me waitlist · {destination.name}</span>
            <div className="flex gap-2">
              {entries.length > 0 && (
                <Button size="sm" variant="outline" onClick={exportCsv} className="border-[#E5E3DB] dark:border-zinc-700" data-testid="waitlist-export">
                  <Download className="w-3 h-3 mr-1" /> CSV
                </Button>
              )}
              {pending > 0 && (
                <Button
                  size="sm"
                  onClick={blast}
                  disabled={blasting || !isActive}
                  className="bg-[#2C5F2D] text-white disabled:opacity-50"
                  data-testid="waitlist-blast"
                  title={!isActive ? "City must be ACTIVE to send launch emails" : ""}
                >
                  {blasting ? "Sending…" : <><Bell className="w-3 h-3 mr-1" /> Send launch email · {pending}</>}
                </Button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        {!isActive && pending > 0 && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-2.5 text-xs text-amber-800 dark:text-amber-300 mb-2">
            <strong>City isn't ACTIVE yet.</strong> Override to ACTIVE (or reach the auto-threshold) to enable the launch-email blast. Setting ACTIVE via override auto-fires emails as a convenience.
          </div>
        )}
        <div className="flex gap-3 text-xs text-[#8A998E] mb-2 flex-wrap">
          <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1.5 align-middle" />{pending} pending</span>
          <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1.5 align-middle" />{notified} notified</span>
        </div>
        {loading ? <div className="py-6 text-center text-[#8A998E]">Loading…</div> : entries.length === 0 ? (
          <div className="py-6 text-center text-[#8A998E] text-sm">No subscribers yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="waitlist-table">
            <thead className="text-xs text-[#8A998E]">
              <tr><th className="text-left py-2">Email</th><th className="text-left">Phone</th><th className="text-left">Travel date</th><th className="text-left">Status</th><th className="text-left">Date</th></tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-[#E5E3DB] dark:border-zinc-800">
                  <td className="py-2">{e.email}</td>
                  <td>{e.phone || "—"}</td>
                  <td>{e.travel_date || "—"}</td>
                  <td>
                    {e.status === "notified" ? (
                      <Badge className="bg-emerald-500 text-white text-[10px]">NOTIFIED</Badge>
                    ) : (
                      <Badge className="bg-amber-500 text-white text-[10px]">PENDING</Badge>
                    )}
                  </td>
                  <td className="text-[10px] text-[#8A998E]">
                    {e.notified_at ? `sent ${new Date(e.notified_at).toLocaleDateString()}` : new Date(e.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DestinationsWidget() {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    api.get("/admin/destinations/dashboard")
      .then((r) => setCounts(r.data))
      .catch(() => {});
  }, []);
  if (!counts) return null;
  const c = counts.counts;
  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-[#E5E3DB] dark:border-zinc-800 p-4 mb-3" data-testid="destinations-widget">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#2C5F2D]" />
          <span className="text-sm font-semibold text-[#262626] dark:text-zinc-100">Destinations</span>
        </div>
        <span className="text-[11px] text-[#8A998E]">{c.total} cities · {counts.waitlist_total} waitlist signups</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-2 text-center" data-testid="widget-active">
          <div className="text-[10px] uppercase text-emerald-700 dark:text-emerald-400 font-bold">Active</div>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-300">{c.active}</div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-2 text-center" data-testid="widget-beta">
          <div className="text-[10px] uppercase text-amber-700 dark:text-amber-400 font-bold">Beta</div>
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-300">{c.beta}</div>
        </div>
        <div className="bg-sky-50 dark:bg-sky-950/30 rounded-lg p-2 text-center" data-testid="widget-coming-soon">
          <div className="text-[10px] uppercase text-sky-700 dark:text-sky-400 font-bold">Coming soon</div>
          <div className="text-2xl font-bold text-sky-600 dark:text-sky-300">{c.coming_soon}</div>
        </div>
        <div className="bg-rose-50 dark:bg-rose-950/30 rounded-lg p-2 text-center" data-testid="widget-disabled">
          <div className="text-[10px] uppercase text-rose-700 dark:text-rose-400 font-bold">Disabled</div>
          <div className="text-2xl font-bold text-rose-600 dark:text-rose-300">{c.disabled}</div>
        </div>
      </div>
    </div>
  );
}
